import { cloneDeep, isEqual } from 'lodash';

import { defineStore } from 'pinia';
import { useArchiveStore } from 'src/stores/archive';
import { DeleteTheIdoCommand } from 'src/stores/ido/commands/DeleteTheIdoCommand';
import { DeleteTheIdoContactPersonCommand } from 'src/stores/ido/commands/DeleteTheIdoContactPersonCommand';
import { LinkTheAggregateToTheIdo } from 'src/stores/ido/commands/LinkTheAggregateToTheIdo';
import { SaveTheIdoCommand } from 'src/stores/ido/commands/SaveTheIdoCommand';
import { SaveTheIdoContactPersonCommand } from 'src/stores/ido/commands/SaveTheIdoContactPersonCommand';
import { UnLinkTheAggregateFromTheIdo } from 'src/stores/ido/commands/UnLinkTheAggregateFromTheIdo';
import {
	GetTheIdoButtonForAnAggregateIdQuery,
	GetTheIdoButtonSearchResultsQuery,
	GetTheIdoJSONModelForAnIdoAndInstanceQuery,
	GetTheIdoRelationsForAnIdoQuery,
	GetTheIdoRelationsListviewModelAndDataQuery,
	GetThePhoneCallCallerNameForAContactPersonInAnIdoAndInstanceQuery,
	GetThePhoneCallCallerNameForAnIdoAndInstanceQuery,
} from 'src/stores/ido/queries';
import { v4 as uuidV4 } from 'uuid';

import {
	GetTheIdoButtonSearchResultsQueryReturn,
	GetTheIdoButtonSearchResultsQueryReturnRunType,
	GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturn,
	GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturnRunType,
	IdoButton,
	IdoButtonRunType,
	IdoRecord,
	IdoRecordRunType,
	IdoRelationsForAnIdo,
	IdoRelationsForAnIdoRunType,
	LinkedIdo,
} from 'src/models/ido';
import { IdoListViewModel, IdoListViewModelRunType } from 'src/models/idoList';

import { IdoMerger } from 'src/util/ido/IdoMerger';

import {
	CreateIdoRecordCallback,
	GetTheIdoButtonForAnAggregateIdQueryCallback,
	GetTheIdoButtonSearchResultsQueryCallback,
	GetTheIdoJSONModelForAnIdoAndInstanceQueryCallback,
	GetTheIdoRelationsForAnIdoQueryCallback,
	GetTheIdoRelationsListviewModelAndDataQueryCallback,
	GetThePhoneCallCallerNameForAContactPersonInAnIdoAndInstanceQueryCallback,
	GetThePhoneCallCallerNameForAnIdoAndInstanceQueryCallback,
} from './callbacks';
import {
	AddMemoryLinkedIdoPayload,
	ClearMemoryIdoButtonPayload,
	CreateIdoRecordPayload,
	DeleteTheIdoContactPersonPayload,
	DeleteTheIdoPayload,
	DeleteTheIdoResult,
	FetchIdoButtonForAnAggregateIdPayload,
	FetchIdoRecordPayload,
	FetchIdoRelationsForAnIdoIdoInstanceListViewModelPayload,
	FetchIdoRelationsForAnIdoPayload,
	FetchThePhoneCallCallerNameForAContactPersonInAnIdoAndInstancePayload,
	FetchThePhoneCallCallerNameForAnIdoAndInstancePayload,
	IdoStoreState,
	LinkTheAggregateToTheIdoPayload,
	RemoveMemoryLinkedIdoPayload,
	ResetPayload,
	SaveTheIdoContactPersonPayload,
	SaveTheIdoPayload,
	SaveTheIdoResult,
	SearchIdoButtonPayload,
	SetIdoButtonSearchReturnPayload,
	SetIdoRelationsForAnIdoIdoInstanceListViewModelLoadingPayload,
	SetIdoRelationsForAnIdoIdoInstanceListViewModelPayload,
	SetIdoRelationsForAnIdoIdoInstanceListViewModelResultCountPayload,
	SetIdoRelationsForAnIdoLoadingPayload,
	SetIdoRelationsForAnIdoPayload,
	SetIdoRelationsPaginationPayload,
	SetMemorySearchResultsPayload,
	SetThePhoneCallCallerNameForAContactPersonInAnIdoAndInstancePayload,
	SetThePhoneCallCallerNameForAnIdoAndInstancePayload,
	UnLinkTheAggregateFromTheIdoPayload,
	UpdateLastKnownChangeTimestampPayload,
	UpdateTheIdoModelValuePayload,
} from './interfaces';
import { Pagination } from './types';

const createDefaultPagination = (): Pagination => ({
	page: 1,
	rowsNumber: 0,
	rowsPerPage: 20,
	sort: {
		direction: 'ASC',
		property: 'ID',
	},
});

const createDefaultState = (): IdoStoreState => ({
	idoButtonMemorySearchResults: new Map<string, GetTheIdoButtonSearchResultsQueryReturn[]>(),
	idoButtonSearchRequestVersions: new Map<string, number>(),
	idoButtonSearchResults: new Map<string, GetTheIdoButtonSearchResultsQueryReturn[]>(),
	idoRecords: new Map<string, IdoRecord>(),
	idoRelationsForAnIdo: new Map<string, IdoRelationsForAnIdo[]>(),
	idoRelationsForAnIdoIdoInstanceListViewModel: new Map<string, IdoListViewModel['results'][number]>(),
	idoRelationsForAnIdoIdoInstanceListViewModelLoading: new Map<string, boolean>(),
	idoRelationsForAnIdoIdoInstanceListViewModelResultCount: new Map<string, number>(),
	idoRelationsForAnIdoLoading: new Map<string, boolean>(),
	idoRelationsPagination: new Map<string, Pagination>(),
	initialIdoRecords: new Map<string, IdoRecord>(),
	memoryIdoButton: new Map<string, IdoButton>(),
	memoryIdoLinks: new Map<string, LinkedIdo[]>(),
	newIdoRecordId: null,
	newIdoRecordInstanceId: null,
	phoneCallCallerNameForAnContactPersonsInTheIdoAndInstance: new Map<string, Map<string, string>>(),
	phoneCallCallerNameForAnIdoAndInstance: new Map<string, string>(),
	unavailableIdoRecordIdentifiers: new Set<string>(),
});

const getIdoRecordIdentifier = (record: IdoRecord): string => {
	const jsonModel = record.jsonModel as NonNullable<IdoRecord['jsonModel']>;
	return `${jsonModel.idoId}-${jsonModel.idoInstanceId}`;
};

const createMemoryIdoButton = (idoButtonIdentifier: string): IdoButton => ({
	aggregateId: idoButtonIdentifier,
	aggregateVersion: 1,
	changeable: true,
	idoCount: 0,
	idoWindow: {
		aggregateId: idoButtonIdentifier,
		aggregateVersion: 1,
		idoWindowIdoInstances: [],
		processId: '',
	},
	processId: '',
	render: false,
});

export const useIdoStore = defineStore('ido', {
	actions: {
		addMemoryLinkedIdo(payload: AddMemoryLinkedIdoPayload): void {
			const processId = uuidV4();
			let memoryIdoButton = this.memoryIdoButton.get(payload.idoButtonIdentifier);

			if (memoryIdoButton === undefined) {
				memoryIdoButton = {
					aggregateId: payload.idoButtonIdentifier,
					aggregateVersion: 1,
					changeable: true,
					idoCount: 0,
					idoWindow: {
						aggregateId: payload.idoButtonIdentifier,
						aggregateVersion: 1,
						idoWindowIdoInstances: [],
						processId: processId,
					},
					processId: processId,
					render: true,
				};
			}

			memoryIdoButton.processId = processId;

			let idoWindowIdoInstance = memoryIdoButton.idoWindow.idoWindowIdoInstances.find(
				(idoWindowInstance) => idoWindowInstance.title === payload.linkedIdo.value.instanceTitle
			);

			const idoWindowIdoInstanceIndex = memoryIdoButton.idoWindow.idoWindowIdoInstances.findIndex(
				(idoWindowInstance) => idoWindowInstance.title === payload.linkedIdo.value.instanceTitle
			);

			if (idoWindowIdoInstance === undefined) {
				idoWindowIdoInstance = {
					aggregateId: payload.linkedIdo.value.aggregateId,
					aggregateVersion: 1,
					color: payload.linkedIdo.value.instanceColor,
					idoWindowIdos: [],
					processId: processId,
					title: payload.linkedIdo.value.instanceTitle,
				};
			}

			idoWindowIdoInstance.idoWindowIdos.push({
				aggregateId: payload.linkedIdo.value.aggregateId,
				aggregateVersion: 1,
				displayName: payload.linkedIdo.value.idoDisplayName,
				processId: processId,
				title: payload.linkedIdo.value.idoTitle,
			});

			if (idoWindowIdoInstanceIndex === -1) {
				memoryIdoButton.idoWindow.idoWindowIdoInstances.push(idoWindowIdoInstance);
			} else {
				memoryIdoButton.idoWindow.idoWindowIdoInstances[idoWindowIdoInstanceIndex] = idoWindowIdoInstance;
			}

			let idoCount = 0;
			memoryIdoButton.idoWindow.idoWindowIdoInstances.forEach((instance) => {
				instance.idoWindowIdos.forEach(() => {
					idoCount += 1;
				});
			});
			memoryIdoButton.idoCount = idoCount;

			this.memoryIdoButton.set(payload.idoButtonIdentifier, memoryIdoButton);

			let memoryIdoLinks = this.memoryIdoLinks.get(payload.idoButtonIdentifier);
			if (memoryIdoLinks === undefined) {
				memoryIdoLinks = [];
			}

			memoryIdoLinks.push(payload.linkedIdo);
			this.memoryIdoLinks.set(payload.idoButtonIdentifier, memoryIdoLinks);
		},
		clearIdoButtonSearch(aggregateId: string): void {
			const currentRequestVersion = this.idoButtonSearchRequestVersions.get(aggregateId) ?? 0;
			this.idoButtonSearchRequestVersions.set(aggregateId, currentRequestVersion + 1);
			this.idoButtonSearchResults.set(aggregateId, []);
		},
		clearMemoryIdoButton(payload: ClearMemoryIdoButtonPayload): void {
			this.memoryIdoButton.delete(payload.idoButtonIdentifier);
			this.memoryIdoLinks.delete(payload.idoButtonIdentifier);
			this.idoButtonMemorySearchResults.delete(payload.idoButtonIdentifier);
			this.idoButtonSearchRequestVersions.delete(payload.idoButtonIdentifier);
			this.idoButtonSearchResults.delete(payload.idoButtonIdentifier);
		},
		clearUnavailableIdoRecord(idoRecordIdentifier: string): void {
			this.unavailableIdoRecordIdentifiers.delete(idoRecordIdentifier);
		},
		createIdoRecord(payload: CreateIdoRecordPayload): void {
			if (payload.queryStore === undefined) {
				this.setIdoRecord(null);
				return;
			}

			void payload.queryStore.query<IdoRecord[]>({
				callback: new CreateIdoRecordCallback(
					(idoRecord) => this.setUnsavedIdoRecord(idoRecord),
					(newIdoRecordId) => this.setNewIdoRecordId(newIdoRecordId),
					(newIdoRecordInstanceId) => this.setNewIdoRecordInstanceId(newIdoRecordInstanceId),
					(createdIdoId, createdIdoInstanceId) =>
						void this.fetchIdoRecord({
							baseUrlOfTheClassicVersion: payload.baseUrlOfTheClassicVersion,
							idoId: createdIdoId,
							idoInstanceId: createdIdoInstanceId,
							masterDataExtJSTabTitle: payload.masterDataExtJSTabTitle,
							pongId: payload.pongId,
							queryStore: payload.queryStore,
						})
				),
				createTime: new Date(),
				query: new GetTheIdoJSONModelForAnIdoAndInstanceQuery(
					'',
					payload.idoInstanceId,
					payload.baseUrlOfTheClassicVersion,
					payload.masterDataExtJSTabTitle,
					payload.pongId,
					payload.referencedIdo ?? null
				),
				responseType: 'json',
				runType: IdoRecordRunType,
			});
		},
		async deleteTheIdo(payload: DeleteTheIdoPayload): Promise<DeleteTheIdoResult> {
			const response = await payload.commandBus?.sendCommand<{
				data?: {
					staleData?: boolean;
				};
				success?: boolean;
			}>(new DeleteTheIdoCommand(payload.idoId, payload.idoInstanceId, payload.lastKnownChangeTimestamp));

			if (response?.data === undefined || response.data.success === false) {
				return { staleData: false, success: false };
			}

			if (response.data.data?.staleData === true) {
				return { staleData: true, success: true };
			}

			this.reset({
				idoId: payload.idoId,
				idoInstanceId: payload.idoInstanceId,
				idoRecordIdentifierOrAggregateId: `${payload.idoId}-${payload.idoInstanceId}`,
			});

			return { staleData: false, success: true };
		},
		deleteTheIdoContactPerson(payload: DeleteTheIdoContactPersonPayload): void {
			void payload.commandBus?.sendCommand(
				new DeleteTheIdoContactPersonCommand(payload.idoId, payload.idoInstanceId, payload.contactPersonId)
			);
		},
		async fetchIdoButtonForAnAggregateId(
			payload: FetchIdoButtonForAnAggregateIdPayload
		): Promise<IdoButton | null> {
			if (payload.queryStore === undefined) {
				return null;
			}

			return await new Promise<IdoButton | null>((resolve) => {
				let isResolved = false;
				const resolveIdoButton = (idoButton: IdoButton | null): void => {
					if (isResolved) {
						return;
					}

					isResolved = true;
					resolve(idoButton);
				};

				void payload.queryStore
					.query<IdoButton>({
						callback: new GetTheIdoButtonForAnAggregateIdQueryCallback(
							(idoButton) => resolveIdoButton(idoButton),
							() => resolveIdoButton(null)
						),
						createTime: new Date(),
						query: new GetTheIdoButtonForAnAggregateIdQuery(payload.aggregateId),
						responseType: 'json',
						runType: IdoButtonRunType,
					})
					.then(() => resolveIdoButton(null));
			});
		},
		fetchIdoRecord(payload: FetchIdoRecordPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.setIdoRecord(null);
				return Promise.resolve();
			}

			const idoRecordIdentifier = `${payload.idoId}-${payload.idoInstanceId}`;

			return payload.queryStore.query<IdoRecord[]>({
				callback: new GetTheIdoJSONModelForAnIdoAndInstanceQueryCallback(
					(idoRecord) => {
						this.clearUnavailableIdoRecord(idoRecordIdentifier);
						this.setIdoRecord(idoRecord);
					},
					() => this.setUnavailableIdoRecord(idoRecordIdentifier)
				),
				createTime: new Date(),
				query: new GetTheIdoJSONModelForAnIdoAndInstanceQuery(
					payload.idoId,
					payload.idoInstanceId,
					payload.baseUrlOfTheClassicVersion,
					payload.masterDataExtJSTabTitle,
					payload.pongId
				),
				responseType: 'json',
				runType: IdoRecordRunType,
			});
		},
		fetchIdoRelationsForAnIdo(payload: FetchIdoRelationsForAnIdoPayload): void {
			const idoRecordIdentifier = `${payload.idoId}-${payload.idoInstanceId}`;

			if (payload.queryStore === undefined) {
				this.setIdoRelationsForAnIdo({
					idoRecordIdentifier: idoRecordIdentifier,
					idoRelationsForAnIdo: [],
				});
				this.setIdoRelationsForAnIdoLoading({
					idoRecordIdentifier: idoRecordIdentifier,
					loading: false,
				});
				return;
			}

			this.setIdoRelationsForAnIdoLoading({
				idoRecordIdentifier: idoRecordIdentifier,
				loading: true,
			});

			void payload.queryStore.query<IdoRelationsForAnIdo[]>({
				callback: new GetTheIdoRelationsForAnIdoQueryCallback(
					idoRecordIdentifier,
					(params) => this.setIdoRelationsForAnIdo(params),
					(params) => this.setIdoRelationsForAnIdoLoading(params)
				),
				createTime: new Date(),
				query: new GetTheIdoRelationsForAnIdoQuery(payload.idoId, payload.idoInstanceId),
				responseType: 'json',
				runType: IdoRelationsForAnIdoRunType,
			});
		},
		fetchIdoRelationsForAnIdoIdoInstanceListViewModel(
			payload: FetchIdoRelationsForAnIdoIdoInstanceListViewModelPayload
		): void {
			if (payload.queryStore === undefined) {
				return;
			}

			const idoRecordIdentifier = `${payload.idoInstanceId}-${payload.idoLinkingsParameter.idoId}-${payload.idoLinkingsParameter.instanceId}`;

			this.setIdoRelationsForAnIdoIdoInstanceListViewModelLoading({
				idoRecordIdentifier: idoRecordIdentifier,
				loading: true,
			});

			void payload.queryStore.query<IdoListViewModel>({
				callback: new GetTheIdoRelationsListviewModelAndDataQueryCallback(
					payload.idoInstanceId,
					payload.idoLinkingsParameter,
					(identifier) => this.getIdoRelationsPagination(identifier),
					(params) => this.setIdoRelationsForAnIdoIdoInstanceListViewModel(params),
					(params) => this.setIdoRelationsForAnIdoIdoInstanceListViewModelLoading(params),
					(params) => this.setIdoRelationsForAnIdoIdoInstanceListViewModelResultCount(params),
					(params) => this.setIdoRelationsPagination(params)
				),
				createTime: new Date(),
				query: new GetTheIdoRelationsListviewModelAndDataQuery(
					payload.idoLinkingsParameter,
					payload.idoInstanceId,
					payload.page,
					payload.limit,
					payload.sort
				),
				responseType: 'json',
				runType: IdoListViewModelRunType,
			});
		},
		fetchThePhoneCallCallerNameForAContactPersonInAnIdoAndInstance(
			payload: FetchThePhoneCallCallerNameForAContactPersonInAnIdoAndInstancePayload
		): void {
			if (payload.queryStore === undefined) {
				this.setThePhoneCallCallerNameForAContactPersonInAnIdoAndInstance({
					callerName: '',
					callerNumber: payload.callerNumber,
					idoRecordIdentifierOrAggregateId: `${payload.idoId}-${payload.idoInstanceId}`,
				});
				return;
			}

			void payload.queryStore.query<GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturn>({
				callback: new GetThePhoneCallCallerNameForAContactPersonInAnIdoAndInstanceQueryCallback(
					payload.callerNumber,
					`${payload.idoId}-${payload.idoInstanceId}`,
					(params) => this.setThePhoneCallCallerNameForAContactPersonInAnIdoAndInstance(params)
				),
				createTime: new Date(),
				query: new GetThePhoneCallCallerNameForAContactPersonInAnIdoAndInstanceQuery(
					payload.idoId,
					payload.idoInstanceId,
					payload.contactPersonId,
					payload.callerNumber
				),
				responseType: 'json',
				runType: GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturnRunType,
			});
		},
		fetchThePhoneCallCallerNameForAnIdoAndInstance(
			payload: FetchThePhoneCallCallerNameForAnIdoAndInstancePayload
		): void {
			if (payload.queryStore === undefined) {
				this.setThePhoneCallCallerNameForAnIdoAndInstance({
					callerName: '',
					idoRecordIdentifierOrAggregateId: `${payload.idoId}-${payload.idoInstanceId}`,
				});
				return;
			}

			void payload.queryStore.query<GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturn>({
				callback: new GetThePhoneCallCallerNameForAnIdoAndInstanceQueryCallback(
					`${payload.idoId}-${payload.idoInstanceId}`,
					(params) => this.setThePhoneCallCallerNameForAnIdoAndInstance(params)
				),
				createTime: new Date(),
				query: new GetThePhoneCallCallerNameForAnIdoAndInstanceQuery(
					payload.idoId,
					payload.idoInstanceId,
					payload.callerNumber
				),
				responseType: 'json',
				runType: GetThePhoneCallCallerNameForAnIdoAndInstanceQueryReturnRunType,
			});
		},
		getIdoRelationsForAnIdo(idoRecordIdentifier: string): IdoRelationsForAnIdo[] | undefined {
			return this.idoRelationsForAnIdo.get(idoRecordIdentifier);
		},
		getIdoRelationsForAnIdoIdoInstanceListViewModel(
			idoRecordIdentifier: string
		): IdoListViewModel['results'][number] | undefined {
			return this.idoRelationsForAnIdoIdoInstanceListViewModel.get(idoRecordIdentifier);
		},
		getIdoRelationsForAnIdoIdoInstanceListViewModelResultCount(idoRecordIdentifier: string): number {
			return this.idoRelationsForAnIdoIdoInstanceListViewModelResultCount.get(idoRecordIdentifier) ?? 0;
		},
		getIdoRelationsPagination(idoRecordIdentifier: string): Pagination {
			return this.idoRelationsPagination.get(idoRecordIdentifier) ?? createDefaultPagination();
		},
		getMemoryIdoButton(idoButtonIdentifier: string): IdoButton {
			return this.memoryIdoButton.get(idoButtonIdentifier) ?? createMemoryIdoButton(idoButtonIdentifier);
		},
		getMemoryLinkedIdos(idoButtonIdentifier: string): LinkedIdo[] | undefined {
			return this.memoryIdoLinks.get(idoButtonIdentifier);
		},
		getPhoneCallCallerNameForAContactPersonInAnIdoAndInstance(
			idoRecordIdentifier: string,
			callerNumber: string
		): string {
			const callerNames = this.phoneCallCallerNameForAnContactPersonsInTheIdoAndInstance.get(idoRecordIdentifier);

			if (callerNames?.has(callerNumber)) {
				return callerNames.get(callerNumber) as string;
			}

			return '';
		},
		getPhoneCallCallerNameForAnIdoAndInstance(idoRecordIdentifier: string): string | undefined {
			return this.phoneCallCallerNameForAnIdoAndInstance.get(idoRecordIdentifier);
		},
		idoButtonSearchReturn(aggregateId: string): GetTheIdoButtonSearchResultsQueryReturn[] | undefined {
			return this.idoButtonSearchResults.get(aggregateId);
		},
		idoRecord(idoRecordIdentifier: string): IdoRecord | undefined {
			return this.idoRecords.get(idoRecordIdentifier);
		},
		initialIdoRecord(idoRecordIdentifier: string): IdoRecord | undefined {
			return this.initialIdoRecords.get(idoRecordIdentifier);
		},
		isIdoRecordUnavailable(idoRecordIdentifier: string): boolean {
			return this.unavailableIdoRecordIdentifiers.has(idoRecordIdentifier);
		},
		isIdoRelationsForAnIdoIdoInstanceListViewModelLoading(idoRecordIdentifier: string): boolean {
			return this.idoRelationsForAnIdoIdoInstanceListViewModelLoading.get(idoRecordIdentifier) ?? false;
		},
		isIdoRelationsForAnIdoLoading(idoRecordIdentifier: string): boolean {
			return this.idoRelationsForAnIdoLoading.get(idoRecordIdentifier) ?? false;
		},
		isUnsavedIdoRecord(idoRecordIdentifier: string): boolean {
			return (
				this.idoRecords.has(idoRecordIdentifier) === true &&
				this.initialIdoRecords.has(idoRecordIdentifier) === false
			);
		},
		linkTheAggregateToTheIdo(payload: LinkTheAggregateToTheIdoPayload) {
			const aggregate = JSON.parse(payload.aggregateId) as {
				moduleName: string;
			};
			const archiveStore = useArchiveStore();

			switch (aggregate.moduleName) {
				case 'module_note':
				case 'module_task':
				case 'module_message':
				case 'module_document':
					archiveStore.archiveSelectedAggregateIds({
						aggregateIds: [payload.aggregateId],
						commandBus: payload.commandBus,
					});
					break;
			}

			return payload.commandBus?.sendCommand(
				new LinkTheAggregateToTheIdo(payload.aggregateId, payload.idoId, payload.idoInstanceId)
			);
		},
		markIdoAsSaved(idoRecordIdentifier: string): void {
			const idoRecordFromCollection = this.idoRecords.get(idoRecordIdentifier);
			if (idoRecordFromCollection !== undefined) {
				this.initialIdoRecords.set(idoRecordIdentifier, cloneDeep<IdoRecord>(idoRecordFromCollection));
			}
		},
		removeMemoryLinkedIdo(payload: RemoveMemoryLinkedIdoPayload): void {
			const memoryIdoButton = this.memoryIdoButton.get(payload.idoButtonIdentifier);

			if (memoryIdoButton !== undefined) {
				const idoWindowIdoInstance = memoryIdoButton.idoWindow.idoWindowIdoInstances.find(
					(idoWindowInstance) => idoWindowInstance.title === payload.linkedIdo.value.instanceTitle
				);

				const idoWindowIdoInstanceIndex = memoryIdoButton.idoWindow.idoWindowIdoInstances.findIndex(
					(idoWindowInstance) => idoWindowInstance.title === payload.linkedIdo.value.instanceTitle
				);

				if (idoWindowIdoInstance !== undefined) {
					idoWindowIdoInstance.idoWindowIdos = idoWindowIdoInstance.idoWindowIdos.filter((idoWindowIdo) => {
						return (
							JSON.stringify({
								aggregateId: idoWindowIdo.aggregateId,
								aggregateVersion: 1,
								displayName: idoWindowIdo.displayName,
								title: idoWindowIdo.title,
							}) !==
							JSON.stringify({
								aggregateId: payload.linkedIdo.value.aggregateId,
								aggregateVersion: 1,
								displayName: payload.linkedIdo.value.idoDisplayName,
								title: payload.linkedIdo.value.idoTitle,
							})
						);
					});

					if (idoWindowIdoInstance.idoWindowIdos.length === 0) {
						memoryIdoButton.idoWindow.idoWindowIdoInstances.splice(idoWindowIdoInstanceIndex, 1);
					} else {
						memoryIdoButton.idoWindow.idoWindowIdoInstances[idoWindowIdoInstanceIndex] =
							idoWindowIdoInstance;
					}

					let idoCount = 0;
					memoryIdoButton.idoWindow.idoWindowIdoInstances.forEach((instance) => {
						instance.idoWindowIdos.forEach(() => {
							idoCount += 1;
						});
					});

					memoryIdoButton.idoCount = idoCount;
				}
			}

			let memoryIdoLinks = this.memoryIdoLinks.get(payload.idoButtonIdentifier);
			if (memoryIdoLinks !== undefined) {
				memoryIdoLinks = memoryIdoLinks.filter((currentLinkedIdo) => currentLinkedIdo !== payload.linkedIdo);
				this.memoryIdoLinks.set(payload.idoButtonIdentifier, memoryIdoLinks);
			}
		},
		reset(payload: ResetPayload): void {
			this.idoRecords.delete(payload.idoRecordIdentifierOrAggregateId);
			this.phoneCallCallerNameForAnIdoAndInstance.delete(payload.idoRecordIdentifierOrAggregateId);
			this.phoneCallCallerNameForAnContactPersonsInTheIdoAndInstance.delete(
				payload.idoRecordIdentifierOrAggregateId
			);
			this.initialIdoRecords.delete(payload.idoRecordIdentifierOrAggregateId);
			this.newIdoRecordId = null;
			this.newIdoRecordInstanceId = null;
			this.idoButtonSearchResults.delete(payload.idoRecordIdentifierOrAggregateId);
			this.unavailableIdoRecordIdentifiers.delete(payload.idoRecordIdentifierOrAggregateId);

			const idoRecordIdentifier = `-${payload.idoId}-${payload.idoInstanceId}`;

			const remainingIdoRelationsForAnIdo = new Map<string, IdoRelationsForAnIdo[]>();
			this.idoRelationsForAnIdo.forEach((value, key) => {
				if (key !== payload.idoRecordIdentifierOrAggregateId) {
					remainingIdoRelationsForAnIdo.set(key, value);
				}
			});
			this.idoRelationsForAnIdo = remainingIdoRelationsForAnIdo;

			const remainingIdoRelationsForAnIdoIdoInstanceListViewModel = new Map<
				string,
				IdoListViewModel['results'][number]
			>();
			this.idoRelationsForAnIdoIdoInstanceListViewModel.forEach((value, key) => {
				if (!new RegExp(`${idoRecordIdentifier}$`).test(key)) {
					remainingIdoRelationsForAnIdoIdoInstanceListViewModel.set(key, value);
				}
			});
			this.idoRelationsForAnIdoIdoInstanceListViewModel = remainingIdoRelationsForAnIdoIdoInstanceListViewModel;

			const remainingIdoRelationsForAnIdoIdoInstanceListViewModelResultCount = new Map<string, number>();
			this.idoRelationsForAnIdoIdoInstanceListViewModelResultCount.forEach((value, key) => {
				if (!new RegExp(`${idoRecordIdentifier}$`).test(key)) {
					remainingIdoRelationsForAnIdoIdoInstanceListViewModelResultCount.set(key, value);
				}
			});
			this.idoRelationsForAnIdoIdoInstanceListViewModelResultCount =
				remainingIdoRelationsForAnIdoIdoInstanceListViewModelResultCount;

			const remainingIdoRelationsPagination = new Map<string, Pagination>();
			this.idoRelationsPagination.forEach((value, key) => {
				if (!new RegExp(`${idoRecordIdentifier}$`).test(key)) {
					remainingIdoRelationsPagination.set(key, value);
				}
			});
			this.idoRelationsPagination = remainingIdoRelationsPagination;
		},
		async saveTheIdo(payload: SaveTheIdoPayload): Promise<SaveTheIdoResult> {
			const response = await payload.commandBus?.sendCommand<{
				data?: {
					lastKnownChangeTimestamp?: string;
					staleData?: boolean;
				};
				success?: boolean;
			}>(
				new SaveTheIdoCommand(
					payload.idoId,
					payload.idoInstanceId,
					payload.lastKnownChangeTimestamp ?? '',
					payload.jsonPayload
				)
			);

			if (response?.data === undefined || response.data.success === false) {
				return { staleData: false, success: false };
			}

			const saveData = response.data.data;
			const staleData = saveData?.staleData === true;
			const lastKnownChangeTimestamp = saveData?.lastKnownChangeTimestamp;

			if (!staleData) {
				this.updateLastKnownChangeTimestamp({
					idoRecordIdentifier: `${payload.idoId}-${payload.idoInstanceId}`,
					lastKnownChangeTimestamp: lastKnownChangeTimestamp ?? '',
				});
			}

			return { lastKnownChangeTimestamp, staleData, success: true };
		},
		saveTheIdoContactPerson(payload: SaveTheIdoContactPersonPayload): void {
			void payload.commandBus?.sendCommand(
				new SaveTheIdoContactPersonCommand(
					String(payload.idoId),
					String(payload.idoInstanceId),
					payload.jsonPayload
				)
			);
		},
		async searchIdoButton(payload: SearchIdoButtonPayload): Promise<void> {
			const currentRequestVersion = this.idoButtonSearchRequestVersions.get(payload.aggregateId) ?? 0;
			const searchRequestVersion = currentRequestVersion + 1;

			this.idoButtonSearchRequestVersions.set(payload.aggregateId, searchRequestVersion);

			if (payload.queryStore === undefined) {
				this.clearIdoButtonSearch(payload.aggregateId);
				return;
			}

			await payload.queryStore.query<GetTheIdoButtonSearchResultsQueryReturn[]>({
				callback: new GetTheIdoButtonSearchResultsQueryCallback(payload.aggregateId, (params) => {
					if (this.idoButtonSearchRequestVersions.get(payload.aggregateId) !== searchRequestVersion) {
						return;
					}

					this.setIdoButtonSearchReturn(params);
				}),
				createTime: new Date(),
				query: new GetTheIdoButtonSearchResultsQuery(payload.query, payload.aggregateId),
				responseType: 'json',
				runType: GetTheIdoButtonSearchResultsQueryReturnRunType,
			});
		},
		setIdoButtonSearchReturn(payload: SetIdoButtonSearchReturnPayload): void {
			this.idoButtonSearchResults.set(payload.aggregateId, payload.idoButtonSearchResults);
		},
		setIdoRecord(payload: IdoRecord | null): void {
			if (payload === null || payload.jsonModel === undefined || payload.jsonModel === null) {
				return;
			}

			const idoRecordIdentifier = getIdoRecordIdentifier(payload);
			this.clearUnavailableIdoRecord(idoRecordIdentifier);
			const idoRecordFromCollection = this.idoRecords.get(idoRecordIdentifier);
			const initialLoadedIdoRecordFromCollection = this.initialIdoRecords.get(idoRecordIdentifier);

			if (idoRecordFromCollection !== undefined && isEqual(idoRecordFromCollection, payload)) {
				return;
			}

			if (idoRecordFromCollection !== undefined && initialLoadedIdoRecordFromCollection !== undefined) {
				const mergedIdoRecord = IdoMerger.merge(
					initialLoadedIdoRecordFromCollection,
					idoRecordFromCollection,
					payload
				) as IdoRecord;

				if (mergedIdoRecord?.jsonModel !== undefined) {
					mergedIdoRecord.jsonModel.model.contactPersons = payload.jsonModel.model.contactPersons;
				}

				if (mergedIdoRecord?.jsonModel !== undefined) {
					const mergedIdoRecordIdentifier = getIdoRecordIdentifier(mergedIdoRecord);
					this.idoRecords.set(idoRecordIdentifier, cloneDeep<IdoRecord>(mergedIdoRecord));
					this.initialIdoRecords.set(mergedIdoRecordIdentifier, cloneDeep<IdoRecord>(payload));
				}
			} else {
				this.idoRecords.set(idoRecordIdentifier, payload);
				this.initialIdoRecords.set(idoRecordIdentifier, cloneDeep<IdoRecord>(payload));
			}
		},
		setIdoRelationsForAnIdo(payload: SetIdoRelationsForAnIdoPayload): void {
			this.idoRelationsForAnIdo.set(payload.idoRecordIdentifier, payload.idoRelationsForAnIdo);
		},
		setIdoRelationsForAnIdoIdoInstanceListViewModel(
			payload: SetIdoRelationsForAnIdoIdoInstanceListViewModelPayload
		): void {
			this.idoRelationsForAnIdoIdoInstanceListViewModel.set(
				payload.idoRecordIdentifier,
				payload.idoRelationsForAnIdoIdoInstanceListViewModel
			);
		},
		setIdoRelationsForAnIdoIdoInstanceListViewModelLoading(
			payload: SetIdoRelationsForAnIdoIdoInstanceListViewModelLoadingPayload
		): void {
			this.idoRelationsForAnIdoIdoInstanceListViewModelLoading.set(payload.idoRecordIdentifier, payload.loading);
		},
		setIdoRelationsForAnIdoIdoInstanceListViewModelResultCount(
			payload: SetIdoRelationsForAnIdoIdoInstanceListViewModelResultCountPayload
		): void {
			this.idoRelationsForAnIdoIdoInstanceListViewModelResultCount.set(
				payload.idoRecordIdentifier,
				payload.idoRelationsForAnIdoIdoInstanceListViewModelResultCount
			);
		},
		setIdoRelationsForAnIdoLoading(payload: SetIdoRelationsForAnIdoLoadingPayload): void {
			this.idoRelationsForAnIdoLoading.set(payload.idoRecordIdentifier, payload.loading);
		},
		setIdoRelationsPagination(payload: SetIdoRelationsPaginationPayload): void {
			this.idoRelationsPagination.set(payload.idoRecordIdentifier, payload.pagination);
		},
		setMemorySearchResults(payload: SetMemorySearchResultsPayload): void {
			this.idoButtonMemorySearchResults.set(payload.aggregateId, payload.idoButtonSearchResults);
		},
		setNewIdoRecordId(newIdoRecordId: null | number): void {
			this.newIdoRecordId = newIdoRecordId;
		},
		setNewIdoRecordInstanceId(newIdoRecordInstanceId: null | number): void {
			this.newIdoRecordInstanceId = newIdoRecordInstanceId;
		},
		setThePhoneCallCallerNameForAContactPersonInAnIdoAndInstance(
			payload: SetThePhoneCallCallerNameForAContactPersonInAnIdoAndInstancePayload
		): void {
			let callerNames = cloneDeep(
				this.phoneCallCallerNameForAnContactPersonsInTheIdoAndInstance.get(
					payload.idoRecordIdentifierOrAggregateId
				)
			);

			if (!callerNames) {
				callerNames = new Map<string, string>();
			}

			callerNames.set(payload.callerNumber, payload.callerName);
			this.phoneCallCallerNameForAnContactPersonsInTheIdoAndInstance.set(
				payload.idoRecordIdentifierOrAggregateId,
				callerNames
			);
		},
		setThePhoneCallCallerNameForAnIdoAndInstance(
			payload: SetThePhoneCallCallerNameForAnIdoAndInstancePayload
		): void {
			this.phoneCallCallerNameForAnIdoAndInstance.set(
				payload.idoRecordIdentifierOrAggregateId,
				payload.callerName
			);
		},
		setUnavailableIdoRecord(idoRecordIdentifier: string): void {
			this.idoRecords.delete(idoRecordIdentifier);
			this.initialIdoRecords.delete(idoRecordIdentifier);
			this.unavailableIdoRecordIdentifiers.add(idoRecordIdentifier);
		},
		setUnsavedIdoRecord(payload: IdoRecord | null): void {
			if (payload === null || payload.jsonModel === undefined || payload.jsonModel === null) {
				return;
			}

			const idoRecordIdentifier = getIdoRecordIdentifier(payload);
			this.clearUnavailableIdoRecord(idoRecordIdentifier);
			this.idoRecords.set(idoRecordIdentifier, payload);
			this.initialIdoRecords.delete(idoRecordIdentifier);
		},
		unLinkTheAggregateFromTheIdo(payload: UnLinkTheAggregateFromTheIdoPayload) {
			if (payload.idoLinkCount === 0) {
				const aggregate = JSON.parse(payload.aggregateId) as {
					moduleName: string;
				};
				const archiveStore = useArchiveStore();

				switch (aggregate.moduleName) {
					case 'module_note':
					case 'module_task':
					case 'module_message':
					case 'module_document':
						archiveStore.deArchiveSelectedAggregateIds({
							aggregateIds: [payload.aggregateId],
							commandBus: payload.commandBus,
						});
						break;
				}
			}

			return payload.commandBus?.sendCommand(
				new UnLinkTheAggregateFromTheIdo(payload.aggregateId, payload.idoId, payload.idoInstanceId)
			);
		},
		updateLastKnownChangeTimestamp(payload: UpdateLastKnownChangeTimestampPayload): void {
			const idoRecordFromCollection = this.idoRecords.get(payload.idoRecordIdentifier);
			if (idoRecordFromCollection !== undefined && idoRecordFromCollection.jsonModel !== undefined) {
				idoRecordFromCollection.jsonModel.lastKnownChangeTimestamp = payload.lastKnownChangeTimestamp;
				this.idoRecords.set(payload.idoRecordIdentifier, idoRecordFromCollection);
			}
		},
		updateTheIdoModelValue(payload: UpdateTheIdoModelValuePayload): void {
			const idoRecordFromCollection = this.idoRecords.get(payload.idoRecordIdentifier);

			if (idoRecordFromCollection !== undefined && idoRecordFromCollection.jsonModel !== undefined) {
				[
					...idoRecordFromCollection.jsonModel.model.leftColumn,
					...idoRecordFromCollection.jsonModel.model.rightColumn,
				].forEach((currentIdoFieldValue) => {
					if (currentIdoFieldValue.name === payload.idoFieldName) {
						switch (currentIdoFieldValue.type) {
							case 'contactPerson':
								currentIdoFieldValue.selectedValue =
									(payload.idoFieldValue as string | undefined) ?? '';
								break;
							case 'singleChoiceSelect':
							case 'singleChoiceRadio':
								currentIdoFieldValue.selectedValue = payload.idoFieldValue as string | undefined;
								break;
							case 'multiplechoice':
							case 'multiplechoiceTag':
							case 'checkboxgroup':
								currentIdoFieldValue.selectedValues = Array.isArray(payload.idoFieldValue)
									? payload.idoFieldValue
									: undefined;
								break;
							case 'textField':
							case 'datefield':
							case 'timefield':
							case 'textarea':
								currentIdoFieldValue.value = (payload.idoFieldValue as string | undefined) ?? '';
								break;
						}
					}
				});

				this.idoRecords.set(payload.idoRecordIdentifier, idoRecordFromCollection);
			}
		},
	},
	state: createDefaultState,
});
export * from './interfaces';
export * from './types';
